import classNames from 'classnames';
import React, {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  HTMLAttributes,
  ReactNode,
  Ref,
  useCallback,
  useId,
} from 'react';

import styles from './FormTextInput.module.css';
import { HelperText } from '../HelperText/HelperText';
import { Icon } from '../Icon';
import { Input, InputType } from '../Input';
import { Label } from '../Label';

export type InputStatus = 'default' | 'error' | 'info' | 'success' | 'alert';

export interface FormTextInputProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style'
> {
  /**
   * The initial string value shown in the input field (for uncontrolled state management).
   */
  defaultValue?: string;
  /**
   * Assistive description text to be shown for the input field.
   */
  description?: ReactNode | undefined | null;
  /**
   * Whether or not the input field is disabled.
   */
  disabled?: boolean;
  /**
   * Icon to be shown in the input field.
   */
  icon?: string;
  /**
   * The ID for the input element. It is added to the input element
   * and not to the container. Should be used for accessibility linking.
   */
  id?: string;
  /**
   * The unique ID to be provided for the input DOM element.
   * It will also be used for accessibility linking.
   * If not provided, one is generated.
   */
  inputId?: string | undefined | null;
  /**
   * The label text to be shown for the input.
   */
  label: string;
  /**
   * Whether to visually hide the label, except for accessibility (e.g. screen readers)
   */
  labelHidden?: boolean;
  /**
   * Maximum allowed length of the input with HTML5 validation.
   */
  maxLength?: number;
  /**
   * Contains any dynamic message shown below the text field.
   * It's style depends on the `status` prop.
   */
  message?: ReactNode;
  /**
   * Minimum allowed length of the input with HTML5 validation.
   */
  minLength?: number;
  /**
   * A name of the form input element.
   */
  name?: string;
  /**
   * The event handler when the input field looses focus.
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * The event handler when the value on the input field changes.
   * NOTE: If you are only interested in the new value, you can use `onValueChange` instead!
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
   * The event handler when the input field is focused.
   */
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /**
   * The event handler when the value on the input field changes.
   * This one takes the new value as the first parameter and the event as the second parameter.
   * It's easier to use than `onChange`.
   */
  onValueChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Required regexp pattern for valid values with HTML5 validation.
   */
  pattern?: string;
  /**
   * Placeholder text to show when the value is empty.
   */
  placeholder?: string;
  /**
   * Whether or not the input field is read only and cannot be edited.
   */
  readOnly?: boolean;
  /**
   * Whether or not to make the input required with HTML5 validation.
   */
  required?: boolean;
  /**
   * Whether or not to show the colon in the label text (without making it part of screen readers)
   */
  showColon?: boolean;
  /**
   * The current status of the field.
   */
  status?: InputStatus;
  /**
   * Sets tooltips text shown in the label
   */
  tooltipText?: string;
  /**
   * Type of the input field. Only text input types are allowed.
   * Defaults to `text`.
   */
  type?: InputType;
  /**
   * String value shown in the input field (for controlled state management).
   */
  value?: string;
}

export const FormTextInput = React.forwardRef(
  (props: FormTextInputProps, ref: Ref<HTMLInputElement>) => {
    const {
      label,
      description,
      status = 'default',
      message,
      labelHidden,
      className,
      style,
      required,
      disabled,
      tooltipText,
      showColon,
      icon,
      onChange,
      onValueChange,
      ...inputProps
    } = props;
    const generatedInputId = useId();
    const inputId = inputProps.id ?? generatedInputId;
    const descriptionId = `${inputId}-description`;
    const messageId = `${inputId}-message`;
    const invalid = status === 'error';
    const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        onValueChange?.(event.currentTarget.value, event);

        onChange?.(event);
      },
      [onChange, onValueChange],
    );

    return (
      <div
        className={classNames(styles.field, className)}
        style={style}
      >
        {!labelHidden && (
          <Label
            disabled={disabled}
            for={inputId}
            required={required}
            showColon={showColon}
            tooltipText={tooltipText}
          >
            {label}
          </Label>
        )}
        <Input
          addOn={icon && <Icon name={icon} />}
          aria-describedby={
            classNames(descriptionId, !invalid && message && messageId) || undefined
          }
          aria-errormessage={invalid && message ? messageId : undefined}
          aria-invalid={invalid}
          aria-label={labelHidden ? label : undefined}
          disabled={disabled}
          id={inputId}
          onChange={onInputChange}
          ref={ref}
          required={required}
          {...inputProps}
        />
        {description && (
          <HelperText
            id={descriptionId}
            showIcon={false}
            variant="assistive"
          >
            {description}
          </HelperText>
        )}
        {message && (
          <HelperText
            id={messageId}
            role={status === 'error' ? 'alert' : undefined}
            showIcon
            variant={status === 'default' ? 'assistive' : status}
          >
            {message}
          </HelperText>
        )}
      </div>
    );
  },
);
